import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface GraphControlsProps {
  showManualLinks: boolean;
  showAIConnections: boolean;
  showFlowJobs: boolean;
  similarityThreshold: number;
  onToggleManualLinks: (value: boolean) => void;
  onToggleAIConnections: (value: boolean) => void;
  onToggleFlowJobs: (value: boolean) => void;
  onSimilarityThresholdChange: (value: number) => void;
  groupBy?: 'none' | 'folder' | 'project' | 'client';
  onGroupByChange?: (value: 'none' | 'folder' | 'project' | 'client') => void;
  filterProject?: string | null;
  onFilterProjectChange?: (value: string | null) => void;
  availableProjects?: Array<{ id: string; name: string }>;
  stats?: {
    nodeCount: number;
    linkCount: number;
    manualLinks: number;
    aiConnections: number;
    flowJobConnections: number;
  };
}

export function GraphControls({
  showManualLinks,
  showAIConnections,
  showFlowJobs,
  similarityThreshold,
  onToggleManualLinks,
  onToggleAIConnections,
  onToggleFlowJobs,
  onSimilarityThresholdChange,
  groupBy = 'none',
  onGroupByChange,
  filterProject = null,
  onFilterProjectChange,
  availableProjects = [],
  stats
}: GraphControlsProps) {
  return (
    <div className="p-4 space-y-4">
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-sidebar-foreground">Graph Controls</h3>
        
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Switch
              id="manual-links"
              checked={showManualLinks}
              onCheckedChange={onToggleManualLinks}
            />
            <Label htmlFor="manual-links" className="text-sm cursor-pointer">
              Manual Links
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="ai-connections"
              checked={showAIConnections}
              onCheckedChange={onToggleAIConnections}
            />
            <Label htmlFor="ai-connections" className="text-sm cursor-pointer">
              AI Connections
            </Label>
          </div>
          {/* Flow-Jobs removed - Basecamp todos aren't nodes, so connections don't render */}
          {/* Keeping toggle hidden for now, can remove later if not needed */}
          {false && (
            <div className="flex items-center gap-2">
              <Switch
                id="flow-jobs"
                checked={showFlowJobs}
                onCheckedChange={onToggleFlowJobs}
              />
              <Label htmlFor="flow-jobs" className="text-sm cursor-pointer">
                Flow-Jobs
              </Label>
            </div>
          )}
        </div>
      </div>

      {showAIConnections && (
        <div className="space-y-2">
          <Label className="text-sm">
            Similarity: {Math.round(similarityThreshold * 100)}%
          </Label>
          <input
            type="range"
            min="0.2"
            max="1.0"
            step="0.05"
            value={similarityThreshold}
            onChange={(e) => {
              const newValue = parseFloat(e.target.value);
              console.log('[Graph Controls] Threshold changed:', newValue);
              onSimilarityThresholdChange(newValue);
            }}
            className="w-full"
          />
          <div className="text-xs text-sidebar-foreground/50 mt-1">
            Lower = more connections (20% minimum)
          </div>
        </div>
      )}

      {/* Grouping Controls */}
      {onGroupByChange && (
        <div className="space-y-2 pt-2 border-t border-sidebar-border">
          <Label className="text-sm">Group By</Label>
          <Select 
            value={groupBy} 
            onValueChange={(value: 'none' | 'folder' | 'project' | 'client') => {
              console.log('[Graph Controls] Group by changed:', value);
              onGroupByChange(value);
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="folder">Folder</SelectItem>
              <SelectItem value="project">Project</SelectItem>
              <SelectItem value="client">Client</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Filter by Project */}
      {onFilterProjectChange && availableProjects.length > 0 && (
        <div className="space-y-2 pt-2 border-t border-sidebar-border">
          <Label className="text-sm">Filter Project</Label>
          <Select 
            value={filterProject || 'all'} 
            onValueChange={(value) => {
              const newFilter = value === 'all' ? null : value;
              console.log('[Graph Controls] Filter project changed:', { from: filterProject, to: newFilter });
              onFilterProjectChange(newFilter);
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects ({availableProjects.length})</SelectItem>
              {availableProjects.map(project => (
                <SelectItem key={project.id} value={project.id}>
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {stats && (
        <div className="text-xs text-sidebar-foreground/50 space-y-1 pt-2 border-t border-sidebar-border">
          <div>{stats.nodeCount} nodes</div>
          <div>{stats.linkCount} links</div>
          {stats.manualLinks > 0 && <div>{stats.manualLinks} manual</div>}
          {stats.aiConnections > 0 && <div>{stats.aiConnections} AI</div>}
          {stats.flowJobConnections > 0 && <div>{stats.flowJobConnections} flow-jobs</div>}
        </div>
      )}

      {/* Legend */}
      <div className="space-y-2 pt-2 border-t border-sidebar-border">
        <h4 className="text-xs font-semibold text-sidebar-foreground/70">Node Types</h4>
        <div className="space-y-1.5 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded"></div>
            <span className="text-sidebar-foreground/70">Work Spec</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-yellow-400 rounded"></div>
            <span className="text-sidebar-foreground/70">Note</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
            <span className="text-sidebar-foreground/70">Voice Memo</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gray-500 rounded"></div>
            <span className="text-sidebar-foreground/70">Basecamp Todo</span>
          </div>
        </div>
        
        <h4 className="text-xs font-semibold text-sidebar-foreground/70 mt-3">Link Types</h4>
        <div className="space-y-1.5 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-6 h-0.5 bg-blue-500"></div>
            <span className="text-sidebar-foreground/70">Manual Link</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-0.5 border-t-2 border-dotted border-purple-500"></div>
            <span className="text-sidebar-foreground/70">AI Connection</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-0.5 border-t-2 border-dashed border-green-500"></div>
            <span className="text-sidebar-foreground/70">Flow-Job</span>
          </div>
        </div>
      </div>
    </div>
  );
}

